import React, { useContext, useMemo } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { Icon } from "@culturehq/components";
import { ElementSource } from "chq";

import { Trans } from "app/utils";

import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { StoryModalContext } from "components/stories/video-studio/contexts/StoryContext";
import { deleteSections, isSubtitleText } from "components/stories/video-studio/sidebar/tabs/subtitle/utils";
import { VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import { isStoryElement } from "components/stories/video-studio/utils";
import ContextMenuOption from "./ContextMenuOption";

interface DeleteOptionProps {
  currentElement: ElementState;
  onSetContextMenuVisible: (value: boolean) => void;
  preview: Preview;
}

const DeleteOption: React.FC<DeleteOptionProps> = ({
  currentElement,
  onSetContextMenuVisible,
  preview
}) => {
  const { data, handleUpdateData } = useContext(CreatomateContext);
  const { onSelectStories, selectedStories } = useContext(StoryModalContext);
  const { activeElementsId } = data;

  const { elements } = preview.state || { elements: [] };
  const allSubtitleElements = useMemo(() => elements.filter(
    element => isSubtitleText(element.source.name)), [elements]
  );

  const deleteElement = async () => {
    const source = preview.getSource();
    const { elements } = source;

    if (elements) {
      const elementsToKeep = elements.filter((element: any) => (
        !activeElementsId.includes(element.id)
      ));

      await preview.setSource({
        ...source,
        elements: elementsToKeep
      }, true);
    }
  };

  const handleDeleteElement = async () => {
    if (!activeElementsId.includes(currentElement.source.id)) {
      preview.setActiveElements([currentElement.source.id]);
    }
    await deleteElement();
    const isVideoType = currentElement?.source?.type === VIDEO_TYPE;
    if (isVideoType && isStoryElement(currentElement.source)) {
      const source = preview.getSource();
      const subtitleElements: ElementSource[] = [];
      if (allSubtitleElements.length > 0) {
        subtitleElements.push(...deleteSections(allSubtitleElements, currentElement));
      }
      if (subtitleElements.length === 0) handleUpdateData({ blockSubtitles: true });
      const noSubtitleElements = source.elements.filter(
        (element: ElementSource) => !isSubtitleText(element.name)
      );
      // We have to do this because if the duration was set and you delete a video,
      // the duration should change dynamically
      await preview.setSource({
        ...source,
        elements: [...noSubtitleElements, ...subtitleElements],
        duration: null
      }, false);
    }

    if (selectedStories.length > 0) {
      onSelectStories(selectedStories.filter(
        oldStory => oldStory.creatomateId !== currentElement.source.id
      ));
    }
    onSetContextMenuVisible(false);
  };

  return (
    <ContextMenuOption onClick={handleDeleteElement}>
      <Icon className="Delete-Element" icon="trash-a-outlined" />
      <Trans>Delete</Trans>
    </ContextMenuOption>
  );
};

export default DeleteOption;
